/**
 * Sale Price Display
 * Reads the active promo from #sale-promo-config. When the active promo has
 * `showDiscountedPrice: true`, every displayed product/cart/subtotal price on
 * the site is visually augmented with:
 *   <s>original</s>  <discounted>  [CODE badge]
 * The strike-through is purely cosmetic — the real discount only applies when
 * the customer enters the promo code at checkout. Checkout buttons on the site
 * are rewritten to `/discount/<CODE>?redirect=/checkout` so the code is auto-
 * applied when the customer proceeds to checkout.
 *
 * Config is re-read on every scheduled apply, so flipping the theme-editor
 * setting applies (or undoes) the effect live without a page refresh.
 */
(function() {
  'use strict';

  var CT_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit'
  });

  function getTodayCT() {
    var parts = CT_FORMATTER.formatToParts(new Date());
    var get = function(type) { return parts.find(function(p) { return p.type === type; }).value; };
    return get('year') + '-' + get('month') + '-' + get('day');
  }

  function readPromos() {
    var el = document.getElementById('sale-promo-config');
    if (!el) return [];
    try { return (JSON.parse(el.textContent).promos) || []; }
    catch (e) { return []; }
  }

  function findActivePromo(promos) {
    var preview = promos.find(function(p) { return p.previewMode; });
    if (preview) return preview;
    var today = getTodayCT();
    return promos.find(function(p) {
      return p.startDate && p.endDate && today >= p.startDate && today <= p.endDate;
    }) || null;
  }

  var DEFAULT_ACCENT = '#00A4E0';

  // `pct` drives the scheduled-promo overlay; `amountCents` drives the flat
  // dollars-off GiveAFriend overlay. Exactly one is ever non-zero.
  var OFF_STATE = { active: false, pct: 0, amountCents: 0, code: '', badgeLabel: '', accent: DEFAULT_ACCENT, source: '' };

  function readPromoState() {
    var promo = findActivePromo(readPromos());
    if (!promo || !promo.showDiscountedPrice) return null;
    // Buy More Save More applies a real tiered discount automatically at
    // checkout. Layering this flat-% overlay on top would double-count the
    // discount in the UI, so the overlay stays off whenever that mode is on.
    if (promo.buyMoreSaveMoreMode) return null;
    var pct = parseInt(promo.discountPercent, 10);
    if (!pct || pct <= 0 || pct >= 100) return null;
    var code = (promo.promoCode || '').trim();
    if (!code) return null;
    // The badge shows the code to enter at checkout.
    return { active: true, pct: pct, amountCents: 0, code: code, badgeLabel: code, accent: promo.accentColor || DEFAULT_ACCENT, source: 'promo' };
  }

  // GiveAFriend subscription opt-in (snippets/gaf-subscription-optin.liquid).
  // Ticking the box takes a flat dollar amount off the animal (the discount app
  // takes it off the highest-priced animal in the cart), so the PDP previews it
  // on the animal selector's price and the subtotal (see GAF_OPTIN_SELECTORS):
  // struck-through prices, no code (the discount app applies it automatically)
  // and no badge/label anywhere on the PDP. On a product that's already on sale
  // the theme's own compare-at strike is kept and this overlay's strike is
  // dropped (see transformElement) so only one slash-through shows: retail ->
  // final price.
  function readGafOptinState() {
    var el = document.getElementById('gaf-optin');
    if (!el) return null;
    var checkbox = el.querySelector('[data-gaf-optin-checkbox]');
    if (!checkbox || !checkbox.checked) return null;
    var dollars = parseInt(el.dataset.discountAmount, 10);
    if (!dollars || dollars <= 0) return null;
    return { active: true, pct: 0, amountCents: dollars * 100, code: '', badgeLabel: '', accent: DEFAULT_ACCENT, source: 'gafOptin' };
  }

  // A scheduled promo wins: both discounts rendered at once would double-count
  // a saving the customer only gets once.
  function readConfigState() {
    return readPromoState() || readGafOptinState() || OFF_STATE;
  }

  function sameState(a, b) {
    return a.active === b.active && a.pct === b.pct && a.amountCents === b.amountCents &&
      a.code === b.code && a.badgeLabel === b.badgeLabel && a.accent === b.accent &&
      a.source === b.source;
  }

  // Money helpers
  function parseMoney(str) {
    var m = String(str).match(/\$(\d{1,3}(?:,\d{3})*(?:\.\d+)?)/);
    if (!m) return null;
    return { raw: m[0], cents: Math.round(parseFloat(m[1].replace(/,/g, '')) * 100), index: m.index };
  }

  function formatMoney(cents) {
    var d = (cents / 100).toFixed(2);
    return '$' + d.replace(/\.00$/, '');
  }

  // Selector list: elements whose rendered text contains a "current" price
  // that should receive the fake-sale treatment. Elements that are themselves
  // strike-through compare-at prices (<s>, <del>) are skipped in shouldSkip().
  var SELECTORS = [
    // Collection / product card current prices
    '.price-item--sale',
    '.price__regular dd .price-item--regular',
    '.sale.igPrice',
    '.product-item__price',
    // Product detail page
    '#new-pdp-price-current',
    '.new-pdp__accessory-price',
    '#new-pdp-subtotal',
    '[data-onetime-price]',
    '[data-subscribe-price]',
    // Subscription / bundle / upsell PDPs
    '.pdp-price',
    '.pseudo-bundle-price',
    '[data-price]',
    // Cart drawer
    '.cart-drawer__item-price',
    '#cart-drawer-total',
    '.cart-drawer__upsell-price',
    // Upsell modal
    '#upsell-modal__total-price',
    // LP builders
    '#ib__total-price',
    '#cpb__total-price',
    '.ip-lp__hero-price',
    // Quick / featured displays
    '#variantPrice',
    '.upsell-item_money',
    // Cart page upsells
    '#money',
    // Search results (bundle "from $X" + all other price flavors)
    '.search__result-content .ff-body'
  ];

  // The GiveAFriend opt-in only previews a discount on the package being
  // configured, so it stays on the PDP. Cart and checkout are handled by the
  // discount app, and collection cards would be wrong — the discount depends on
  // an opt-in that only exists on this page.
  //
  // The member benefit is a flat amount off the ANIMAL (the discount app takes
  // it off the highest-priced animal in the cart), so it previews on:
  //   - the animal selector's selected price (the switcher trigger) and every
  //     row in its dropdown / bottom sheet — each row is a candidate animal,
  //     and whichever one is picked carries the $10. Scoped by the switcher's
  //     own IDs so the accessory picker, which reuses .new-pdp__accessory-price,
  //     is left alone; accessories aren't discounted, so $10 off each accessory
  //     row would preview a saving the customer never gets.
  //   - the package subtotal, which the animal's $10 flows straight into.
  //   - #new-pdp-price-current for any default-mode product where the opt-in is
  //     shown (harmless no-op on animal/bundle, which have no such element).
  // The saving lands once — on the animal — so both struck figures drop by the
  // same $10 and the rows still reconcile with the subtotal.
  var GAF_OPTIN_SELECTORS = [
    '#new-pdp-price-current',
    '.new-pdp__animal-switcher-trigger .new-pdp__accessory-price',
    '#animal-switcher-list .new-pdp__accessory-price',
    '#animal-sheet-list .new-pdp__accessory-price',
    '#new-pdp-subtotal'
  ];

  // Exclude prices inside these ancestors (historical orders, gift cards,
  // unit pricing, pre-existing compare-at strike-throughs, analytics JSON).
  var EXCLUDE_ANCESTOR_SELECTOR = [
    '.account',
    '.account__order',
    '.account__order-summary',
    '[data-unit-price-container]',
    '.new-pdp__accessory-price--original',
    '.cart-drawer__item-price--original',
    '.cart-drawer__subtotal-price--original',
    '.price__compare',
    '.search__result-sold-out-price',
    // GiveAFriend products are excluded from the sale at the discount-code
    // level, so don't show the fake-sale treatment on them on the front end.
    '[data-product-type*="GiveAFriend"]',
    // The opt-in row quotes the subscription's own price, which is never
    // discounted by either overlay.
    '.new-pdp__gaf-optin',
    'script',
    'noscript'
  ].join(',');

  function shouldSkip(el) {
    if (!el) return true;
    if (el.querySelector && el.querySelector('.fake-sale__new')) return true;
    if (el.tagName === 'S' || el.tagName === 'DEL') return true;
    if (el.closest && el.closest(EXCLUDE_ANCESTOR_SELECTOR)) return true;
    return false;
  }

  function findMoneyTextNode(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: function(n) {
        var p = n.parentNode;
        while (p && p !== el) {
          if (p.tagName === 'S' || p.tagName === 'DEL') return NodeFilter.FILTER_REJECT;
          if (p.classList && p.classList.contains('fake-sale__old')) return NodeFilter.FILTER_REJECT;
          if (p.classList && p.classList.contains('fake-sale__badge')) return NodeFilter.FILTER_REJECT;
          if (p.matches && p.matches(EXCLUDE_ANCESTOR_SELECTOR)) return NodeFilter.FILTER_REJECT;
          p = p.parentNode;
        }
        return /\$\d/.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    return walker.nextNode();
  }

  var state = { active: false, pct: 0, amountCents: 0, code: '', accent: DEFAULT_ACCENT };

  function injectStyles(accent) {
    var existing = document.getElementById('sale-price-display-styles');
    if (existing) existing.remove();
    var css =
      '.fake-sale__old{text-decoration:line-through;opacity:.6;margin-right:.25em;font-weight:inherit;}' +
      '.fake-sale__new{font-weight:inherit;}' +
      '.fake-sale__badge{' +
        'display:inline-block;' +
        'margin-left:.35em;' +
        'padding:1px 5px;' +
        'border:1px dashed ' + accent + ';' +
        'border-radius:3px;' +
        'color:' + accent + ';' +
        'font-size:.72em;' +
        'font-weight:700;' +
        'letter-spacing:.02em;' +
        'line-height:1.2;' +
        'vertical-align:middle;' +
        'white-space:nowrap;' +
      '}' +
      // Accessory/animal/GAF price blocks (both the selected-variant trigger
      // on the main PDP and each row inside the bottom-sheet pickers) already
      // stack price rows vertically. Match that pattern: strike on its own
      // line, discounted price on the next line, badge underneath — all
      // right-aligned. Lock in explicit line-heights so vertical spacing
      // stays consistent across the trigger and both bottom-sheet lists.
      '.new-pdp__accessory-price-wrap .new-pdp__accessory-price{display:block;text-align:right;line-height:1.3;}' +
      '.new-pdp__accessory-price-wrap .fake-sale__old,' +
      '.new-pdp__accessory-price-wrap .fake-sale__new{display:block;margin:0;line-height:1.3;}' +
      '.new-pdp__accessory-price-wrap .fake-sale__badge{display:block;margin:4px 0 0 auto;width:fit-content;line-height:1.2;}';

    // Suppress the theme's native compare-at / "X SAVINGS" displays so the
    // overlay's own fake-sale treatment doesn't double up (e.g.
    // <s>$compare</s> <s>$sale</s> $new).
    //
    // This applies ONLY to the scheduled-promo source. The GiveAFriend opt-in
    // draws nothing in the cart (the discount app renders the REAL line
    // discount there) and nothing on collection cards, so suppressing native
    // displays under it would hide real prices — most visibly the cart
    // drawer's actual discount strikethrough and savings line. Its PDP
    // double-strike is instead avoided per-element in transformElement (keep the
    // native strike, drop ours) rather than by hiding native displays wholesale.
    if (state.source === 'promo') {
      css +=
        'body[data-sale-price-display="active"] .igComparePrice,' +
        'body[data-sale-price-display="active"] .price__compare,' +
        'body[data-sale-price-display="active"] .new-pdp__price--original,' +
        'body[data-sale-price-display="active"] #new-pdp-price-sr-original,' +
        'body[data-sale-price-display="active"] .new-pdp__accessory-price--original,' +
        'body[data-sale-price-display="active"] #new-pdp-subtotal-original,' +
        'body[data-sale-price-display="active"] #new-pdp-subtotal-savings,' +
        'body[data-sale-price-display="active"] .cart-drawer__item-price--original,' +
        'body[data-sale-price-display="active"] .cart-drawer__subtotal-price--original,' +
        'body[data-sale-price-display="active"] #cart-drawer-original-total,' +
        'body[data-sale-price-display="active"] .cart-drawer__savings,' +
        'body[data-sale-price-display="active"] #cart-drawer-savings{display:none !important;}';
    }
    var style = document.createElement('style');
    style.id = 'sale-price-display-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function removeStyles() {
    var existing = document.getElementById('sale-price-display-styles');
    if (existing) existing.remove();
  }

  // The theme renders its own compare-at "was" strikethrough next to a current
  // price whenever a product is on sale (pdp-price-display / switcher-option /
  // subtotal blocks). Returns that native element when it's present and showing
  // a real price, so the GiveAFriend opt-in can keep it and drop its own strike
  // instead of stacking a second slash on top. Sibling of the current price
  // within the same price block, so a parent lookup finds it.
  var NATIVE_COMPARE_SELECTOR =
    '.new-pdp__price--original,.new-pdp__accessory-price--original,.new-pdp__subtotal-price--original';

  function findNativeCompareEl(el) {
    var parent = el.parentElement;
    if (!parent) return null;
    var orig = parent.querySelector(NATIVE_COMPARE_SELECTOR);
    if (!orig || orig.style.display === 'none') return null;
    return parseMoney(orig.textContent) ? orig : null;
  }

  // Roll the theme's native "$X SAVINGS" pill (subtotal only) up to the combined
  // retail -> final-GAF-price saving, so it agrees with the single strikethrough
  // instead of quoting just the pre-existing product sale.
  function updateGafSavingsPill(el, origCents, newCents) {
    var parent = el.parentElement;
    if (!parent) return;
    var pill = parent.querySelector('.new-pdp__subtotal-savings');
    if (!pill) return;
    var save = origCents - newCents;
    if (save <= 0) return;
    pill.textContent = formatMoney(save) + ' SAVINGS';
    pill.style.display = '';
    pill.classList.add('fake-sale__savings-adjusted');
  }

  function transformElement(el) {
    if (shouldSkip(el)) return;
    var textNode = findMoneyTextNode(el);
    if (!textNode) return;

    var parsed = parseMoney(textNode.nodeValue);
    if (!parsed || parsed.cents <= 0) return;

    // Flat dollars off for the GiveAFriend opt-in, percent off for a scheduled
    // promo. A subtotal smaller than the flat amount bails out rather than
    // rendering $0 — the discount app clamps it at checkout anyway.
    var newCents = state.amountCents
      ? parsed.cents - state.amountCents
      : Math.round(parsed.cents * (100 - state.pct) / 100);
    if (newCents <= 0 || newCents >= parsed.cents) return;

    // GiveAFriend opt-in on an already-discounted product: the theme already
    // shows a native compare-at strike (e.g. $56 -> $50.60). Rather than stack a
    // second slash on top ($56, $50.60), keep the native retail strike, hide our
    // own strike, and just swap in the final price -> one "$56 -> $45.54" line.
    var nativeCompareEl = state.source === 'gafOptin' ? findNativeCompareEl(el) : null;

    var before = textNode.nodeValue.slice(0, parsed.index);
    var after = textNode.nodeValue.slice(parsed.index + parsed.raw.length);

    var frag = document.createDocumentFragment();
    if (before) frag.appendChild(document.createTextNode(before));

    var oldS = document.createElement('s');
    oldS.className = 'fake-sale__old';
    oldS.textContent = parsed.raw;
    // Kept in the DOM (so undoAll can restore the price) but hidden — the theme's
    // native compare-at is already the single visible strikethrough.
    if (nativeCompareEl) oldS.style.display = 'none';
    frag.appendChild(oldS);

    var newSpan = document.createElement('span');
    newSpan.className = 'fake-sale__new';
    newSpan.textContent = formatMoney(newCents);
    frag.appendChild(newSpan);

    if (state.badgeLabel) {
      var badge = document.createElement('span');
      badge.className = 'fake-sale__badge';
      badge.textContent = state.badgeLabel;
      frag.appendChild(badge);
    }

    if (after) frag.appendChild(document.createTextNode(after));

    textNode.parentNode.replaceChild(frag, textNode);

    if (nativeCompareEl) {
      var origParsed = parseMoney(nativeCompareEl.textContent);
      if (origParsed) updateGafSavingsPill(el, origParsed.cents, newCents);
    }
  }

  function applyAll() {
    var selectors = state.source === 'gafOptin' ? GAF_OPTIN_SELECTORS : SELECTORS;
    selectors.forEach(function(sel) {
      var nodes;
      try { nodes = document.querySelectorAll(sel); }
      catch (e) { return; }
      nodes.forEach(transformElement);
    });
  }

  function undoAll() {
    document.querySelectorAll('.fake-sale__old').forEach(function(oldEl) {
      var parent = oldEl.parentNode;
      if (!parent) return;
      var original = oldEl.textContent;
      var sibling = oldEl.nextSibling;
      while (sibling) {
        var next = sibling.nextSibling;
        if (sibling.nodeType === 1 &&
            (sibling.classList.contains('fake-sale__new') || sibling.classList.contains('fake-sale__badge'))) {
          parent.removeChild(sibling);
          sibling = next;
          continue;
        }
        break;
      }
      parent.replaceChild(document.createTextNode(original), oldEl);
      parent.normalize();
    });

    // Roll any "$X SAVINGS" pill we bumped to the combined figure back to the
    // theme's native retail->sale saving. Recomputed live from the now-restored
    // prices (not a stored snapshot) so it stays correct across variant switches.
    document.querySelectorAll('.fake-sale__savings-adjusted').forEach(function(pill) {
      pill.classList.remove('fake-sale__savings-adjusted');
      var parent = pill.parentElement;
      if (!parent) return;
      var origEl = parent.querySelector('.new-pdp__subtotal-price--original');
      var curEl = parent.querySelector('.new-pdp__subtotal-price');
      var o = origEl && parseMoney(origEl.textContent);
      var c = curEl && parseMoney(curEl.textContent);
      if (o && c && o.cents > c.cents) {
        pill.textContent = formatMoney(o.cents - c.cents) + ' SAVINGS';
        pill.style.display = '';
      } else {
        pill.style.display = 'none';
      }
    });
  }

  function rewriteCheckoutLinks() {
    if (!state.code) return;
    var newHref = '/discount/' + encodeURIComponent(state.code) + '?redirect=/checkout';
    document.querySelectorAll('a[href]').forEach(function(a) {
      if (a.getAttribute('data-sale-checkout-rewritten') === '1') return;
      var href = a.getAttribute('href');
      if (!href) return;
      if (href === '/checkout' || href.indexOf('/checkout?') === 0) {
        a.setAttribute('data-sale-checkout-original', href);
        a.setAttribute('href', newHref);
        a.setAttribute('data-sale-checkout-rewritten', '1');
      }
    });
  }

  function restoreCheckoutLinks() {
    document.querySelectorAll('a[data-sale-checkout-rewritten="1"]').forEach(function(a) {
      var original = a.getAttribute('data-sale-checkout-original') || '/checkout';
      a.setAttribute('href', original);
      a.removeAttribute('data-sale-checkout-original');
      a.removeAttribute('data-sale-checkout-rewritten');
    });
  }

  function sync() {
    var next = readConfigState();
    if (!sameState(state, next)) {
      // Config changed: tear down old transforms so we can re-apply with new values.
      undoAll();
      restoreCheckoutLinks();
      state = next;
      if (state.active) {
        injectStyles(state.accent);
        document.body.setAttribute('data-sale-price-display', 'active');
      } else {
        removeStyles();
        document.body.removeAttribute('data-sale-price-display');
      }
    }
    if (state.active) {
      applyAll();
      rewriteCheckoutLinks();
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function() {
      scheduled = false;
      sync();
    }, 16);
  }

  // The PDP hides the prices this overlay owns until the first pass has run,
  // so shoppers never see the undiscounted figure repaint (see the
  // `data-sale-pending` gate in sections/product--template.liquid).
  function clearPendingGate() {
    document.documentElement.removeAttribute('data-sale-pending');
  }

  // Callable synchronously — the PDP's in-place product swap runs this in the
  // same task as the DOM replacement so no frame paints the pre-discount price.
  window.SalePriceDisplay = {
    refresh: function() {
      sync();
      clearPendingGate();
    }
  };

  function init() {
    sync();
    clearPendingGate();

    // Re-apply whenever the DOM changes (cart refresh, PDP variant change,
    // dynamically rendered upsells/builders, theme-editor section swaps).
    var observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    // Theme editor events: section load/unload/reorder all imply the config
    // JSON may have changed, so re-sync.
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(function(evt) {
      document.addEventListener(evt, schedule);
    });

    // Ticking a checkbox flips a property, not an attribute, so the mutation
    // observer never sees it — the opt-in snippet announces the change instead.
    document.addEventListener('gaf-optin:change', schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
